import { CartItem, PaymentMethod } from '../models';
import type { FacebookPixelFunction } from './facebookPixel.d';

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID?.trim();

type MetaPixelStandardEvent =
  | 'PageView'
  | 'ViewContent'
  | 'AddToCart'
  | 'InitiateCheckout'
  | 'AddPaymentInfo'
  | 'Purchase';

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  [PaymentMethod.CreditCard]: 'credit_card',
  [PaymentMethod.DebitCard]: 'debit_card',
  [PaymentMethod.Pix]: 'pix',
  [PaymentMethod.Cash]: 'cash',
  [PaymentMethod.CreditStore]: 'credit_store',
  [PaymentMethod.BankTransfer]: 'bank_transfer',
  [PaymentMethod.Promissory]: 'promissory',
};

interface MetaPixelContentLine {
  id: string;
  quantity: number;
  item_price: number;
}

interface MetaPixelAggregatedContents {
  content_ids: string[];
  contents: MetaPixelContentLine[];
  num_items: number;
  value: number;
}

let initialized = false;

function injectPixelScript(): void {
  if (window.fbq) return;

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as unknown as FacebookPixelFunction;

  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];

  window.fbq = fbq;
  window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
}

export function initMetaPixel(): void {
  if (!PIXEL_ID || initialized) return;

  injectPixelScript();
  window.fbq?.('init', PIXEL_ID);
  window.fbq?.('track', 'PageView');
  initialized = true;
}

export function isMetaPixelActive(): boolean {
  return Boolean(PIXEL_ID) && initialized;
}

export function setMetaPixelAdvancedMatching(data: { email?: string | null; phone?: string | null }): void {
  if (!PIXEL_ID || !initialized || !window.fbq) return;

  const em = data.email?.trim().toLowerCase() || undefined;
  const digits = data.phone?.replace(/\D/g, '') ?? '';
  const ph = digits ? (digits.startsWith('55') ? digits : `55${digits}`) : undefined;

  if (!em && !ph) return;

  window.fbq('init', PIXEL_ID, {
    ...(em ? { em } : {}),
    ...(ph ? { ph } : {}),
  });
}

function generateEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function trackMetaEvent(
  name: MetaPixelStandardEvent,
  params?: Record<string, unknown>,
  options?: { eventID?: string }
): string | null {
  if (!PIXEL_ID || !initialized || !window.fbq) return null;

  const eventID = options?.eventID ?? generateEventId();
  window.fbq('track', name, params, { eventID });
  return eventID;
}

export function trackMetaCustomEvent(name: string, params?: Record<string, unknown>): string | null {
  if (!PIXEL_ID || !initialized || !window.fbq) return null;

  const eventID = generateEventId();
  window.fbq('trackCustom', name, params, { eventID });
  return eventID;
}

export function trackMetaPageView(): string | null {
  return trackMetaEvent('PageView');
}

function resolveContentId(id: number, sku?: string | null): string {
  return sku && sku.trim() ? sku : String(id);
}

function aggregateCartItems(items: CartItem[]): MetaPixelAggregatedContents {
  const contents = items.map((item) => ({
    id: resolveContentId(item.productId, item.variantSku),
    quantity: item.quantity,
    item_price: item.promotionalPrice ?? item.price,
  }));

  return {
    content_ids: contents.map((c) => c.id),
    contents,
    num_items: contents.reduce((sum, c) => sum + c.quantity, 0),
    value: contents.reduce((sum, c) => sum + c.item_price * c.quantity, 0),
  };
}

export function trackViewContent(product: {
  id: number;
  sku?: string | null;
  name: string;
  price: number;
}): string | null {
  const id = resolveContentId(product.id, product.sku);
  return trackMetaEvent('ViewContent', {
    content_ids: [id],
    content_type: 'product',
    content_name: product.name,
    contents: [{ id, quantity: 1, item_price: product.price }],
    num_items: 1,
    value: product.price,
    currency: 'BRL',
  });
}

export function trackAddToCart(item: CartItem): string | null {
  const id = resolveContentId(item.productId, item.variantSku);
  const unitPrice = item.promotionalPrice ?? item.price;
  return trackMetaEvent('AddToCart', {
    content_ids: [id],
    content_type: 'product',
    content_name: item.productName,
    contents: [{ id, quantity: item.quantity, item_price: unitPrice }],
    num_items: item.quantity,
    value: unitPrice * item.quantity,
    currency: 'BRL',
  });
}

export function trackInitiateCheckout(items: CartItem[]): string | null {
  const { content_ids, contents, num_items, value } = aggregateCartItems(items);
  return trackMetaEvent('InitiateCheckout', {
    content_ids,
    content_type: 'product',
    contents,
    num_items,
    value,
    currency: 'BRL',
  });
}

export function trackCheckoutStep(step: string, items: CartItem[]): string | null {
  const { num_items, value } = aggregateCartItems(items);
  return trackMetaCustomEvent('CheckoutStep', {
    step,
    num_items,
    value,
    currency: 'BRL',
  });
}

export function trackAddPaymentInfo(
  items: CartItem[],
  payments: { paymentMethod: PaymentMethod; amountPaid: number; installments?: number }[]
): string | null {
  const { content_ids, contents, num_items, value } = aggregateCartItems(items);
  const methods = payments.map((p) => PAYMENT_METHOD_LABELS[p.paymentMethod] ?? String(p.paymentMethod));
  const installments = payments.reduce((max, p) => Math.max(max, p.installments ?? 1), 1);
  return trackMetaEvent('AddPaymentInfo', {
    content_ids,
    content_type: 'product',
    contents,
    num_items,
    value,
    currency: 'BRL',
    payment_method: methods.join('+'),
    payment_methods_count: payments.length,
    installments,
  });
}

export function trackPurchase(orderId: number | string, items: CartItem[]): string | null {
  const { content_ids, contents, num_items, value } = aggregateCartItems(items);
  return trackMetaEvent('Purchase', {
    content_ids,
    content_type: 'product',
    contents,
    num_items,
    value,
    currency: 'BRL',
    order_id: String(orderId),
  });
}
